<script setup lang="ts">
import { useForm, useField } from 'vee-validate'
import { string } from 'yup'
import { trackMyAccountEvent } from '~/helpers/datalayer/user-event'

const emit = defineEmits(['loginSuccess'])

const userStore = useUserStore()
const errorMessage = ref('')
const errorIconSize = ref(0)
const isLoading = ref(false)
const attempt = ref(0)
const uiStore = useUIStore()

const {
  values: formValues,
  validate,
  errors,
} = useForm({
  initialValues: {
    email: '',
    password: '',
  },
})

const { t, locale } = useI18n()
const emailValidate = string().email(t('validation_messages.email')).required(t('validation_messages.requiredEmail'))
const passwordValidate = string()
  .matches(REGEX_PASSWORD, t('validation_messages.password'))
  .required(t('validation_messages.requiredPassword'))

const {
  value: email,
  errorMessage: emailError,
  validate: validateEmail,
} = useField('email', emailValidate, {
  validateOnValueUpdate: false,
})
const {
  value: password,
  errorMessage: passwordError,
  validate: validatePassword,
} = useField('password', passwordValidate, {
  validateOnValueUpdate: false,
})

const { accountUrl, baseSiteUrl } = useSiteUrl()

const scrollToInvalidField = (selector?: string) => scrollToFieldOrLastInvalid('#loginForm', errors.value, selector)
const handleBlurValidation = (validator, selector) => validateOnBlur(validator, selector, '#loginForm', errors.value)

const loginUser = async () => {
  attempt.value++
  const { valid } = await validate()

  if (!valid) {
    scrollToInvalidField()
    return
  }

  trackMyAccountEvent('custom.form.click', { label: 'login', group: 'login page', url: baseSiteUrl }, locale.value)

  isLoading.value = true
  const { error, signIn } = useLogin()
  const loginSuccess = await signIn(formValues.email, formValues.password)
  isLoading.value = false
  if (!loginSuccess) {
    if (error.value?.message.includes('401')) {
      errorMessage.value = t('my_account.login_page.error_message.login_error')
      errorIconSize.value = 80
    } else {
      errorMessage.value = t('my_account.login_page.error_message.login_retry')
      errorIconSize.value = 0
    }
    await nextTick()
    scrollToError(document.querySelector('.login-form__error-message'))
    return
  }

  // FIXME - Shouldn't use userStore.user directly, as it exposes PII such as email and name
  // However, the decision was made to keep the dataLayer implementation as is for now. #237029
  trackMyAccountEvent('custom.user.login', { user: userStore.user }, locale.value)

  trackMyAccountEvent('custom.form.complete', { group: 'login page' }, locale.value)

  emit('loginSuccess')
  window.dispatchEvent(new Event('loginSuccess'))
  uiStore.isShowLoginModal = false
}

const preferencesStore = usePreferencesStore()
</script>

<template>
  <section class="login-form">
    <template v-if="!userStore?.isLoggedIn">
      <h2>{{ $t('my_account.login_page.inputs.login.label') }}</h2>

      <!-- Show error message -->
      <AlertBar
        v-if="errorMessage"
        variant="error"
        v-bind="errorIconSize > 0 ? { 'icon-height': errorIconSize, 'icon-width': errorIconSize } : {}"
        class="login-form__error-message"
      >
        <span>{{ errorMessage }}</span>
      </AlertBar>

      <form @submit.prevent="loginUser" class="login-form__form" id="loginForm" novalidate>
        <Input
          type="email"
          required
          autocomplete="username"
          :label="$t('checkout.shipping.inputs.email.label')"
          name="email"
          input-test-id="login-email"
          v-model="email"
          @blur="handleBlurValidation(validateEmail, 'email')"
          :error="emailError"
          :attempt="attempt"
        />
        <Input
          type="password"
          required
          autocomplete="current-password"
          :label="$t('my_account.login_page.inputs.password.label')"
          name="password"
          input-test-id="login-password"
          v-model="password"
          @blur="handleBlurValidation(validatePassword, 'password')"
          :error="passwordError"
          :attempt="attempt"
        />
        <div class="login-form__forgot-password">
          <Button
            type="button"
            variant="tertiary"
            appearance="Link"
            data-testid="login-forgot-password"
            @click.prevent="uiStore.isShowForgotPasswordModal = true"
            >{{ $t('my_account.login_page.forgot_password.title') }}</Button
          >
        </div>
        <Button
          variant="primary"
          type="submit"
          class="login-form__login-button"
          data-testid="login-submit"
          :disabled="isLoading"
        >
          {{ $t('my_account.login_page.inputs.login.label') }}
        </Button>
      </form>
    </template>

    <LoadingSpinner v-if="isLoading" />

    <div class="login-form__register">
      <!-- @vue-skip -->
      <RichTextPanel
        v-if="preferencesStore?.site?.signUpPanel"
        v-bind="{ ...preferencesStore?.site?.signUpPanel }"
        skip-container
      />
      <template v-else>
        <h2>{{ $t('my_account.login_page.register_user.title') }}</h2>
        <p>
          {{ $t('my_account.login_page.register_user.subtext') }}
        </p>
        <Action variant="secondary" action="mhj-signup-panel">
          {{ $t('my_account.login_page.register_user.create_account') }}
        </Action>
      </template>
    </div>
  </section>
</template>

<style lang="postcss">
.login-form {
  position: relative;
  text-align: left;

  .login-form__form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .login-form__forgot-password {
    margin-bottom: var(--space-30);
  }

  .login-form__login-button {
    width: 100%;
  }

  .login-form__register {
    margin-top: var(--space-100);

    .action {
      width: 100%;
    }
  }

  .login-form__error-message {
    margin-bottom: 1rem;
  }
}
</style>
